<template>
    <div>
        <v-dialog v-model="chooseCsvColumnsDialog" max-width="960" scrollable @input="onCsvDialogToggle">
            <v-card>
                <v-card-title class="pb-2">
                    <span>{{ $t('system.labels.exportToCsv') }}</span>
                    <v-spacer></v-spacer>
                    <v-chip small outlined color="primary" class="font-weight-medium">
                        {{ $t('system.labels.csvColumnsSelected', {
                            selected: selectedCsvColumnsExport.length,
                            total: labelsExportCsv.length,
                        }) }}
                    </v-chip>
                </v-card-title>
                <v-card-subtitle class="pt-0 pb-3">
                    {{ $t('system.labels.csvExportSelectColumns') }}
                </v-card-subtitle>
                <v-divider></v-divider>
                <v-card-text class="pt-4 pb-2">
                    <v-row dense align="center" class="mb-3">
                        <v-col cols="12" sm="7" md="8">
                            <v-text-field
                                v-model="csvColumnFilter"
                                dense
                                outlined
                                hide-details
                                clearable
                                prepend-inner-icon="mdi-magnify"
                                :placeholder="$t('system.labels.csvFilterColumns')"
                            ></v-text-field>
                        </v-col>
                        <v-col cols="12" sm="5" md="4" class="d-flex flex-wrap justify-sm-end">
                            <v-btn text small color="primary" class="px-2"
                                :disabled="labelsExportCsv.length === 0"
                                @click="selectAllCsvColumns">
                                {{ $t('system.labels.csvSelectAllColumns') }}
                            </v-btn>
                            <v-btn text small class="px-2"
                                :disabled="selectedCsvColumnsExport.length === 0"
                                @click="deselectAllCsvColumns">
                                {{ $t('system.labels.csvDeselectAllColumns') }}
                            </v-btn>
                        </v-col>
                    </v-row>
                    <div v-if="labelsExportCsv.length === 0" class="text-center text--secondary py-8">
                        {{ $t('system.labels.csvNoColumnsAvailable') }}
                    </div>
                    <div v-else-if="filteredLabelsExportCsv.length === 0" class="text-center text--secondary py-8">
                        {{ $t('system.labels.csvNoColumnsMatchFilter') }}
                    </div>
                    <div v-else class="csv-columns-panel">
                        <v-row dense>
                            <v-col
                                v-for="item in filteredLabelsExportCsv"
                                :key="item.value"
                                cols="12"
                                sm="6"
                                lg="4"
                            >
                                <v-sheet
                                    outlined
                                    rounded
                                    class="csv-column-item pa-2 d-flex align-center"
                                    :class="{ 'csv-column-item--selected': isCsvColumnSelected(item.value) }"
                                    @click="toggleCsvColumn(item.value)"
                                >
                                    <v-switch
                                        dense
                                        hide-details
                                        class="mt-0 pt-0 flex-grow-0"
                                        :input-value="isCsvColumnSelected(item.value)"
                                        @click.stop
                                        @change="setCsvColumnSelected(item.value, $event)"
                                    ></v-switch>
                                    <span class="csv-column-item__label text-body-2 ml-1">{{ item.text }}</span>
                                </v-sheet>
                            </v-col>
                        </v-row>
                    </div>
                </v-card-text>
                <v-divider></v-divider>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="primary" class="mr-2" @click="chooseCsvColumnsDialog = false">{{ $t('button.cancel') }}</v-btn>
                    <v-btn color="primary" :disabled="selectedCsvColumnsExport.length === 0 || csvExportWorking"
                        :loading="csvExportWorking" @click="confirmCsvExport">
                        {{ $t('button.export') }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-tooltip top>
            <template v-slot:activator="{ on }">
                <v-btn v-on="on" color="primary" dark small class="mb-2 mr-2" fab :disabled="csvExportWorking"
                    @click="openExportCsvDialog">
                    <v-icon>mdi-export-variant</v-icon>
                </v-btn>
            </template>
            <span>{{ $t('system.labels.exportToCsv') }}</span>
        </v-tooltip>
    </div>
</template>

<script>
import apiController from '@/utils/apiController';
import EventBus from '@/utils/eventBus';
import {
    addPendingSchedulerDownload,
    SCHEDULER_DOWNLOAD_TYPES,
} from '@/utils/schedulerDownloadPending';

export default {
    name: "ExportTableCSV",
    data: () => ({
        chooseCsvColumnsDialog: false,
        selectedCsvColumnsExport: [],
        exportCsvRows: null,
        csvExportWorking: false,
        csvColumnFilter: '',
    }),
    props: {
        content: {
            type: Array,
            default() {
                return [];
            }
        },
        headers: {
            type: Array,
            default() {
                return [];
            }
        },
        title: {
            type: String,
            default() {
                return this.$t('system.labels.title');
            }
        },
        filename: {
            type: String,
            default: null,
        },
        /** Se definido, os dados do CSV vêm desta função assíncrona (exportação sob demanda). */
        contentLoader: {
            type: Function,
            default: null,
        },
        /** Monta linhas em fatias com pausas para a UI (grandes volumes). */
        deferProcessing: {
            type: Boolean,
            default: false,
        },
        localRowLimit: {
            type: Number,
            default: 1000,
        },
        /** Path relativo a API_URL usado pela grade (ex.: getallpessoas). Obrigatório para enqueue no scheduler. */
        listEndpoint: {
            type: String,
            default: null,
        },
        exportFilters: {
            type: Object,
            default() {
                return {};
            },
        },
        /** Parâmetros (não SQL) para re-chamar o listEndpoint sem paginação server-side (ex.: { dti, dtf }). */
        requestParams: {
            type: Object,
            default() {
                return {};
            },
        },
        /** Filtros aplicados pelo worker sobre a resposta do listEndpoint (contains/from-to/in/eq). */
        postFilters: {
            type: Object,
            default() {
                return {};
            },
        },
        exportSortBy: {
            type: Array,
            default() {
                return [];
            },
        },
        exportSortDesc: {
            type: Array,
            default() {
                return [];
            },
        },
        /** Total conhecido da grade (paginação server-side), usado para decidir local vs scheduler. */
        exportTotal: {
            type: Number,
            default: 0,
        },
        /** IDs das linhas selecionadas; vazio = exportar todas as linhas dos filtros. */
        selectedIds: {
            type: Array,
            default() {
                return [];
            },
        },
        idField: {
            type: String,
            default: null,
        },
    },
    computed: {
        downloadFilename() {
            return this.filename || this.title || 'export';
        },
        resolvedSelectedIds() {
            return Array.isArray(this.selectedIds)
                ? this.selectedIds.filter((id) => id != null && id !== '')
                : [];
        },
        exportRowCountEstimate() {
            if (this.resolvedSelectedIds.length > 0) {
                return this.resolvedSelectedIds.length;
            }
            const total = Number(this.exportTotal);
            return Number.isFinite(total) && total > 0 ? total : 0;
        },
        canEnqueueSchedulerExport() {
            return !!(this.listEndpoint && String(this.listEndpoint).trim());
        },

        csvDialogHeaderColumns() {
            return (this.headers || []).filter((h) => {
                if (!h || !h.value) return false;
                if (h.value === 'actions' || h.value === 'data-table-expand') return false;
                if (h.align === ' d-none') return false;
                return true;
            });
        },
        labelsExportCsv() {
            const headerCols = this.csvDialogHeaderColumns.map((h) => ({
                text: this.headerLabel(h, h.value),
                value: h.value,
            }));

            if (headerCols.length > 0) {
                return headerCols;
            }

            const source =
                this.exportCsvRows != null && this.exportCsvRows.length > 0
                    ? this.exportCsvRows
                    : (this.content || []);

            if (source.length > 0) {
                return Object.keys(source[0]).map((key) => ({
                    text: this.headerLabel(this.findHeader(key), key),
                    value: key,
                }));
            }
            return [];
        },
        columnsToExport() {
            if (this.selectedCsvColumnsExport.length === 0) {
                return {};
            }

            const exportLabels = {};
            this.selectedCsvColumnsExport.forEach((value) => {
                const header = this.findHeader(value);
                exportLabels[value] = { title: this.headerLabel(header, value) };
            });
            return exportLabels;
        },
        filteredLabelsExportCsv() {
            const query = (this.csvColumnFilter || '').trim().toLowerCase();
            if (!query) {
                return this.labelsExportCsv;
            }
            return this.labelsExportCsv.filter((item) => {
                const label = (item.text || '').toLowerCase();
                const value = (item.value || '').toLowerCase();
                return label.includes(query) || value.includes(query);
            });
        },
        effectiveLocalRowLimit() {
            const limite = Number(this.localRowLimit);
            return Number.isInteger(limite) && limite > 0 ? limite : 1000;
        },
    },
    methods: {
        isCsvColumnSelected(value) {
            return this.selectedCsvColumnsExport.includes(value);
        },
        setCsvColumnSelected(value, selected) {
            const idx = this.selectedCsvColumnsExport.indexOf(value);
            if (selected && idx === -1) {
                this.selectedCsvColumnsExport = [...this.selectedCsvColumnsExport, value];
            } else if (!selected && idx !== -1) {
                this.selectedCsvColumnsExport = this.selectedCsvColumnsExport.filter((v) => v !== value);
            }
        },
        toggleCsvColumn(value) {
            this.setCsvColumnSelected(value, !this.isCsvColumnSelected(value));
        },
        selectAllCsvColumns() {
            this.selectedCsvColumnsExport = this.labelsExportCsv.map((item) => item.value);
        },
        deselectAllCsvColumns() {
            this.selectedCsvColumnsExport = [];
        },
        findHeader(value) {
            if (!this.headers || this.headers.length === 0) {
                return null;
            }
            return this.headers.find((h) => h.value === value || h.key === value) || null;
        },
        isHeaderSelectedForExport(header) {
            if (!header) {
                return true;
            }
            if (header.to_export !== undefined && header.to_export !== null) {
                return Number(header.to_export) !== 0;
            }
            return header.to_show === 1 || header.to_show === true || header.show !== false;
        },
        defaultSelectedCsvColumns() {
            return this.labelsExportCsv
                .filter((item) => {
                    const value = typeof item === 'object' ? item.value : item;
                    return this.isHeaderSelectedForExport(this.findHeader(value));
                })
                .map((item) => (typeof item === 'object' ? item.value : item));
        },
        headerLabel(header, fallback) {
            if (!header) {
                return fallback;
            }
            if (header.label) {
                return header.label;
            }
            if (header.text) {
                return header.text;
            }
            return fallback;
        },
        onCsvDialogToggle(open) {
            if (!open) {
                this.exportCsvRows = null;
                this.csvColumnFilter = '';
            }
        },
        async openExportCsvDialog() {
            this.exportCsvRows = null;
            this.csvColumnFilter = '';
            // Sem headers e sem conteúdo local (dados só via contentLoader): as colunas vêm das linhas carregadas.
            if (this.labelsExportCsv.length === 0 && typeof this.contentLoader === 'function') {
                this.csvExportWorking = true;
                try {
                    await this.loadCsvExportRows();
                } catch (_err) {
                    EventBus.$emit('show-alert', {
                        mensagem: this.$t('system.labels.csvExportFailed'),
                        tipo: 'error',
                    });
                    return;
                } finally {
                    this.csvExportWorking = false;
                }
            }
            this.selectedCsvColumnsExport = this.defaultSelectedCsvColumns();
            this.chooseCsvColumnsDialog = true;
        },
        async loadCsvExportRows() {
            const fetchForExport = typeof this.contentLoader === 'function';
            if (!fetchForExport) {
                this.exportCsvRows = Array.isArray(this.content) ? this.content : [];
                return this.exportCsvRows;
            }
            // Já carregadas ao abrir o diálogo (colunas derivadas das linhas): reaproveita.
            if (this.exportCsvRows != null) {
                return this.exportCsvRows;
            }

            const csvDetail = this.$t('system.labels.exportToCsv');
            this.$emit('export-loading', {
                loading: true,
                message: csvDetail,
                progress: 0,
            });
            try {
                const rows = await this.contentLoader((pct) => {
                    this.$emit('export-loading', {
                        loading: true,
                        message: csvDetail,
                        progress: pct,
                    });
                });
                this.exportCsvRows = Array.isArray(rows) ? rows : [];
                return this.exportCsvRows;
            } catch (e) {
                this.exportCsvRows = [];
                throw e;
            } finally {
                this.$emit('export-loading', { loading: false });
            }
        },
        async confirmCsvExport() {
            if (this.selectedCsvColumnsExport.length === 0 || this.csvExportWorking) {
                return;
            }

            this.csvExportWorking = true;
            let handedOff = false;
            try {
                const shouldEnqueue =
                    this.canEnqueueSchedulerExport &&
                    this.exportRowCountEstimate > this.effectiveLocalRowLimit;

                if (shouldEnqueue) {
                    handedOff = true;
                    await this.enqueueCsvExport();
                    return;
                }

                const rows = await this.loadCsvExportRows();
                if (
                    rows.length > this.effectiveLocalRowLimit &&
                    this.canEnqueueSchedulerExport
                ) {
                    handedOff = true;
                    await this.enqueueCsvExport();
                } else {
                    handedOff = true;
                    this.downloadCsvInBackground();
                }
            } catch (_err) {
                EventBus.$emit('show-alert', {
                    mensagem: this.$t('system.labels.csvExportFailed'),
                    tipo: 'error',
                });
            } finally {
                if (!handedOff) {
                    this.csvExportWorking = false;
                }
            }
        },
        /** Mesma regra do PDF: `exportFormat` no header manda no que vai para a célula. */
        csvCellValue(row, key) {
            const header = this.findHeader(key);
            if (header && typeof header.exportFormat === 'function') {
                const formatado = header.exportFormat(row[key], row);
                return formatado != null ? formatado : '';
            }
            return row[key];
        },
        escapeCsvCell(val) {
            if (val == null || val === '') return '';
            const s = String(val);
            if (/[;"\r\n]/.test(s)) {
                return '"' + s.replace(/"/g, '""') + '"';
            }
            return s;
        },
        async enqueueCsvExport() {
            const columnKeys = this.selectedCsvColumnsExport.slice();
            const columns = columnKeys.map((key) => ({
                key,
                title: (this.columnsToExport[key] && this.columnsToExport[key].title) || key,
            }));
            if (columns.length === 0 || !this.canEnqueueSchedulerExport) return;

            this.csvExportWorking = true;
            try {
                const selectedIds = this.resolvedSelectedIds.slice();
                const data = {
                    filename: this.downloadFilename,
                    listEndpoint: String(this.listEndpoint).trim(),
                    columns,
                    filters: this.exportFilters && typeof this.exportFilters === 'object'
                        ? this.exportFilters
                        : {},
                    sortBy: Array.isArray(this.exportSortBy) ? this.exportSortBy.slice() : [],
                    sortDesc: Array.isArray(this.exportSortDesc)
                        ? this.exportSortDesc.map((d) => d === true || d === 'true')
                        : [],
                    selectedIds,
                };
                if (this.requestParams && typeof this.requestParams === 'object' && Object.keys(this.requestParams).length > 0) {
                    data.requestParams = this.requestParams;
                }
                if (this.postFilters && typeof this.postFilters === 'object' && Object.keys(this.postFilters).length > 0) {
                    data.postFilters = this.postFilters;
                }
                if (this.idField) {
                    data.idField = this.idField;
                }

                const response = await apiController.request(
                    apiController.endpoints.exportTableCsv,
                    { data, silent: true }
                );
                const body = (response && response.data) || {};
                if (body.erro || !body.id_processo || body.status !== 'PENDING') {
                    throw new Error(body.mensagem || 'export failed');
                }

                addPendingSchedulerDownload({
                    id_processo: body.id_processo,
                    type: SCHEDULER_DOWNLOAD_TYPES.TABLE_CSV_EXPORT,
                    meta: { filename: this.downloadFilename },
                });
                this.chooseCsvColumnsDialog = false;
                EventBus.$emit('scheduler-download-enqueued');
                EventBus.$emit('show-alert', {
                    mensagem: this.$t('system.labels.csvExportEnqueued'),
                    tipo: 'info',
                    duracao: 6000,
                });
            } catch (_err) {
                EventBus.$emit('show-alert', {
                    mensagem: this.$t('system.labels.csvExportFailed'),
                    tipo: 'error',
                });
            } finally {
                this.csvExportWorking = false;
            }
        },
        downloadCsvInBackground() {
            const rows = this.exportCsvRows || [];
            const keys = this.selectedCsvColumnsExport;
            if (keys.length === 0) return;

            this.chooseCsvColumnsDialog = false;
            this.csvExportWorking = true;
            const csvDetail = this.$t('system.labels.exportToCsv');
            this.$emit('export-loading', {
                loading: true,
                message: csvDetail,
                progress: 0,
            });

            const sep = ';';
            const labels = this.columnsToExport;
            const headerLine = keys.map((k) => this.escapeCsvCell((labels[k] && labels[k].title) || k)).join(sep);

            const chunkSize = 250;
            const lines = [headerLine];
            let rowIndex = 0;

            const pump = () => {
                try {
                    const end = Math.min(rowIndex + chunkSize, rows.length);
                    for (; rowIndex < end; rowIndex++) {
                        const row = rows[rowIndex];
                        lines.push(keys.map((k) => this.escapeCsvCell(this.csvCellValue(row, k))).join(sep));
                    }
                    const totalRows = rows.length;
                    const pct = totalRows === 0
                        ? 100
                        : Math.min(100, Math.round((rowIndex / totalRows) * 100));
                    this.$emit('export-loading', {
                        loading: true,
                        message: csvDetail,
                        progress: pct,
                    });
                    if (rowIndex < rows.length) {
                        setTimeout(pump, 0);
                        return;
                    }
                    const blob = new Blob(['\ufeff' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `${this.downloadFilename}.csv`;
                    a.click();
                    URL.revokeObjectURL(url);
                } catch (e) {
                    console.error(e);
                }
                this.csvExportWorking = false;
                this.$nextTick(() => {
                    this.$emit('export-loading', { loading: false });
                });
            };

            setTimeout(pump, 0);
        },
    },
};
</script>

<style scoped>
.csv-columns-panel {
    max-height: min(55vh, 520px);
    overflow-y: auto;
    padding-right: 4px;
}

.csv-column-item {
    cursor: pointer;
    transition: border-color 0.15s ease, background-color 0.15s ease;
    min-height: 48px;
}

.csv-column-item--selected {
    border-color: var(--v-primary-base, #1976d2) !important;
    background-color: rgba(25, 118, 210, 0.06);
}

.csv-column-item__label {
    line-height: 1.3;
    word-break: break-word;
}
</style>
